import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { registerPlugin } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Filesystem } from '@capacitor/filesystem';
import { acceptDocument, offerDocument } from './documents';

/**
 * Bills shared to the app on a phone (Smart Bill Import, phase 1):
 *  - Android: the share sheet or "Open with" (SharedFilePlugin copies the
 *    file into the cache and tells us where);
 *  - iOS: "Open in Costline" from the share sheet (the system copies the
 *    file into the app's Inbox and opens a file:// URL).
 * The file is read, deleted, and offered to the import screen.
 */
interface SharedFile {
  path?: string;
  name?: string;
  mime?: string;
  tooLarge?: boolean;
  /** The sender's file could not be read (no permission, or it is gone). */
  failed?: boolean;
}
interface SharedFilePlugin {
  takePending(): Promise<SharedFile>;
  addListener(event: 'fileShared', listener: (file: SharedFile) => void): Promise<{ remove: () => Promise<void> }>;
}
const SharedFile = registerPlugin<SharedFilePlugin>('SharedFile');

function base64ToBytes(data: string): Uint8Array {
  const bin = atob(data);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function readSharedFile(file: SharedFile): Promise<void> {
  const name = file.name ?? 'bill';
  if (file.tooLarge || file.failed) {
    offerDocument({ ok: false, reason: file.tooLarge ? 'tooLarge' : 'unreadable' });
    return;
  }
  if (!file.path) return;
  try {
    const { data } = await Filesystem.readFile({ path: file.path });
    offerDocument(acceptDocument(name, typeof data === 'string' ? base64ToBytes(data) : new Uint8Array(await data.arrayBuffer()), 'share'));
  } catch {
    offerDocument({ ok: false, reason: 'unreadable' });
  } finally {
    // The copy was only for reading: nothing of the bill stays on the device.
    void Filesystem.deleteFile({ path: file.path }).catch(() => {});
  }
}

const nameOf = (url: string) => decodeURIComponent(url.split('/').pop() ?? 'bill');

export function useSharedDocuments(platform: 'android' | 'ios' | 'web') {
  const navigate = useNavigate();
  const go = useRef(navigate);
  go.current = navigate;
  useEffect(() => {
    if (platform === 'web') return;
    const open = async (file: SharedFile) => {
      await readSharedFile(file);
      go.current('/import');
    };
    const handles: Promise<{ remove: () => Promise<void> }>[] = [];
    // A missing or failing plugin must not break the app: sharing into it just does not work then.
    const warn = (e: unknown) => console.warn('[share-in]', e);
    const listen = (h: Promise<{ remove: () => Promise<void> }>) => handles.push(h.catch((e: unknown) => (warn(e), { remove: async () => {} })));
    if (platform === 'android') {
      listen(SharedFile.addListener('fileShared', (f) => void open(f)));
      void SharedFile.takePending()
        .then((f) => (f.path || f.tooLarge || f.failed ? open(f) : undefined))
        .catch(warn);
    } else {
      const fromUrl = (url: string | undefined) => (url?.startsWith('file://') ? open({ path: url, name: nameOf(url) }) : undefined);
      listen(CapApp.addListener('appUrlOpen', ({ url }) => void fromUrl(url)));
      void CapApp.getLaunchUrl()
        .then((l) => fromUrl(l?.url))
        .catch(warn);
    }
    return () => {
      handles.forEach((h) => void h.then((x) => x.remove()));
    };
  }, [platform]);
}
